/**
 * PhoneRecordingSettingsCard — per-customer settings introduced in
 * migration 0059:
 *   * phone_recording_hangup_action — what happens to a recording the caller
 *     was making when they hung up. auto_save (default) keeps the captured
 *     audio in the active library. discard sends it to the recycle bin so
 *     the admin can recover it but it doesn't appear in regular listings.
 *   * recording_recycle_bin_retention_days — how long deleted recordings
 *     stay recoverable. Capped at 90 days at the platform level; the
 *     reseller can set their own per-reseller cap below that.
 *
 * Used on /customers/:id (super_admin / reseller_owner) AND on the
 * customer-scoped settings page /settings/phone-recording (all roles).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { customers } from '@/api/customers';
import type { CustomerRow } from '@/api/customers';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';

export function PhoneRecordingSettingsCard({
  customer,
}: { customer: CustomerRow }): JSX.Element {
  const qc = useQueryClient();
  const [hangupAction, setHangupAction] = useState<'auto_save' | 'discard'>(
    customer.phone_recording_hangup_action ?? 'auto_save',
  );
  const resellerCap = customer.reseller_recycle_bin_retention_days_max ?? 90;
  const storedDays = customer.recording_recycle_bin_retention_days ?? 30;
  const initialDays = Math.min(storedDays, resellerCap);
  const wasClampedByReseller = storedDays > resellerCap;
  const [retentionDays, setRetentionDays] = useState<number>(initialDays);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const save = useMutation({
    mutationFn: () => customers.update(customer.id, {
      phoneRecordingHangupAction: hangupAction,
      recordingRecycleBinRetentionDays: retentionDays,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customer', customer.id] });
      setSavedAt(Date.now());
    },
  });

  const dirty =
    hangupAction !== (customer.phone_recording_hangup_action ?? 'auto_save') ||
    retentionDays !== (customer.recording_recycle_bin_retention_days ?? 30);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Phone recording</CardTitle>
        <p className="text-xs text-muted-foreground mt-1">
          How phone-recorded audio is handled when a caller hangs up mid-recording,
          and how long deleted recordings stay in the recycle bin.
        </p>
      </CardHeader>
      <CardContent className="space-y-5 text-sm">
        <fieldset className="space-y-2">
          <legend className="font-medium">Hangup mid-recording</legend>
          <p className="text-xs text-muted-foreground">
            What to do with audio captured before the caller dropped.
          </p>
          <label className="flex items-start gap-2">
            <input
              type="radio"
              name="hangupAction"
              value="auto_save"
              checked={hangupAction === 'auto_save'}
              onChange={() => setHangupAction('auto_save')}
              className="mt-1"
            />
            <span>
              <span className="font-medium">Auto-save</span>
              <span className="block text-xs text-muted-foreground">
                Save the partial recording into the library. Default.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2">
            <input
              type="radio"
              name="hangupAction"
              value="discard"
              checked={hangupAction === 'discard'}
              onChange={() => setHangupAction('discard')}
              className="mt-1"
            />
            <span>
              <span className="font-medium">Send to recycle bin</span>
              <span className="block text-xs text-muted-foreground">
                Save but mark as deleted; admin can restore from{' '}
                <Link to="/recordings/recycle-bin" className="underline">
                  Recycle Bin
                </Link>{' '}
                if they want it.
              </span>
            </span>
          </label>
        </fieldset>

        <div className="space-y-2">
          <Label htmlFor="retention-days">Recycle-bin retention (days)</Label>
          <p className="text-xs text-muted-foreground">
            How long a deleted recording stays recoverable before the
            auto-purge sweep removes it. Your reseller's cap is {resellerCap} days
            (platform max is 90).
          </p>
          {wasClampedByReseller && (
            <div
              role="status"
              aria-live="polite"
              className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
            >
              Your saved retention was {storedDays} days, but your reseller has
              since lowered the cap to {resellerCap}. The slider is showing the
              new cap — click <strong>Save changes</strong> to bring this account
              into compliance.
            </div>
          )}
          <div className="flex items-center gap-3">
            <input
              id="retention-days"
              type="range"
              min={1}
              max={resellerCap}
              step={1}
              value={retentionDays}
              onChange={(e) => setRetentionDays(Number(e.target.value))}
              className="flex-1"
            />
            <div className="w-20 text-right font-mono tabular-nums">
              {retentionDays} d
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 pt-2">
          <Button size="sm" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save changes'}
          </Button>
          {savedAt && !save.isPending && !dirty && (
            <span className="text-xs text-muted-foreground">Saved.</span>
          )}
          {save.isError && (
            <span role="alert" className="text-xs text-destructive">
              {save.error instanceof Error ? save.error.message : 'Save failed'}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
